import { Link, type LinkProps } from 'react-router';
import { useCallback, useRef, type ReactNode } from 'react';

/**
 * Mapa de rotas para suas funções de import dinâmico.
 * Ao fazer hover, o chunk do módulo é pré-carregado pelo browser.
 */
const routePrefetchMap: Record<string, () => Promise<any>> = {
  '/qualidade': () => import('../pages/QualityDashboard'),
  '/qualidade/fila-auditorias': () => import('../pages/MonitorAuditWorkspace'),
  '/upload-auditoria': () => import('../pages/UploadAudit'),
  '/auditorias': () => import('../pages/Audits'),
  '/questionarios': () => import('../pages/Questionnaires'),
  '/analitica': () => import('../pages/Analytics'),
  '/relatorios': () => import('../pages/Reports'),
  '/treino': () => import('../pages/training/Training'),
  '/centro-treinamento': () => import('../pages/OperatorTrainingHub'),
  '/calendario-treinos': () => import('../pages/training/TrainingCalendar'),
  '/biblioteca-scripts': () => import('../pages/ScriptLibrary'),
  '/feedback-360': () => import('../pages/Feedback360'),
  '/metas': () => import('../pages/Goals'),
  '/ranking': () => import('../pages/Ranking'),
  '/ligas': () => import('../pages/Leagues'),
  '/certificados': () => import('../pages/Certificates'),
  '/operadores': () => import('../pages/Operators'),
  '/comparacao-operadores': () => import('../pages/performance/OperatorComparison'),
  '/dashboard-personalizado': () => import('../pages/CustomizableDashboard'),
  '/notificacoes': () => import('../pages/NotificationCenter'),
  '/historico-evolucao': () => import('../pages/EvolutionHistory'),
  '/dashboard-supervisor': () => import('../pages/SupervisorDashboard'),
  '/dashboard-setores': () => import('../pages/SectorDashboard'),
  '/meu-perfil': () => import('../pages/OperatorProfile'),
  '/admin/usuarios': () => import('../pages/AdminUsers'),
  '/configuracoes': () => import('../pages/Settings'),
};

// Cache de rotas já prefetchadas para não repetir
const prefetchedRoutes = new Set<string>();

interface PrefetchLinkProps extends LinkProps {
  children: ReactNode;
}

/**
 * PrefetchLink - Link com prefetch automático no hover.
 * Ao fazer mouseEnter, dispara o import dinâmico do chunk da página destino,
 * garantindo carregamento instantâneo quando o usuário clicar.
 */
export function PrefetchLink({ to, children, ...props }: PrefetchLinkProps) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = useCallback(() => {
    const path = typeof to === 'string' ? to : '';
    if (prefetchedRoutes.has(path)) return;

    // Delay de 100ms para evitar prefetch em hover acidental
    timeoutRef.current = setTimeout(() => {
      const prefetchFn = routePrefetchMap[path];
      if (prefetchFn) {
        prefetchedRoutes.add(path);
        prefetchFn().catch(() => {
          // Se falhar, remove do cache para tentar novamente
          prefetchedRoutes.delete(path);
        });
      }
    }, 100);
  }, [to]);

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return (
    <Link
      to={to}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {children}
    </Link>
  );
}